import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../../context/UserContext';
import styles from './SpellingModes.module.css';
import AITutor from '../AITutor/AITutor';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL, apiRequest } from '../../config/api';
import Loader from '../common/Loader/Loader';

const DefinitionSpellingMode = () => {
  const { userProgress, loading: userLoading, error: userError, updateProgress } = useUser();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [hintsUsed, setHintsUsed] = useState(0);
  const initialized = useRef(false);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [completingSession, setCompletingSession] = useState(false);

  const navigate = useNavigate();
  const { state } = useLocation();
  const difficulty = state?.difficulty || userProgress?.modeLevels?.definition_spelling || 1;

  useEffect(() => {
    if (!userLoading && userProgress && !initialized.current) {
      startPractice();
      initialized.current = true;
    }
  }, [userLoading, userProgress]);

  useEffect(() => {
    if (questions.length > 0) {
      initializeQuestion();
    }
  }, [currentQuestion, questions]);

  const initializeQuestion = () => {
    setQuestionStartTime(Date.now());
    setUserInput('');
    setShowExplanation(false);
    setFeedback(null);
    setShowHint(false);
    setHintsUsed(0);
  };

  const startPractice = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        mode: 'definition_spelling',
        difficulty: difficulty
      });

      const questionsData = await apiRequest(`/spelling/questions?${params}`);

      const sessionData = await apiRequest('/sessions/start', {
        method: 'POST',
        body: JSON.stringify({
          mode: 'definition_spelling',
          difficulty: difficulty,
          questions: questionsData.map(q => q._id)
        })
      });

      setQuestions(questionsData);
      localStorage.setItem('currentSessionId', sessionData._id);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || !userInput.trim()) return;
    setIsSubmitting(true);

    try {
      const sessionId = localStorage.getItem('currentSessionId');
      const currentQ = questions[currentQuestion];
      const timeSpent = (Date.now() - questionStartTime) / 1000;
      
      const submitResponse = await apiRequest(`/sessions/${sessionId}/answer`, {
        method: 'POST',
        body: JSON.stringify({
          questionId: currentQ._id,
          answer: userInput.trim(),
          timeSpent,
          hintsUsed
        })
      });

      setFeedback({
        isCorrect: submitResponse.isCorrect,
        message: submitResponse.isCorrect ? 'Correct!' : 'Incorrect. Try again!'
      });
      setShowExplanation(true);

    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    setShowExplanation(false);
    
    if (currentQuestion + 1 >= questions.length) {
      try {
        setCompletingSession(true);
        const sessionId = localStorage.getItem('currentSessionId');
        
        const results = await apiRequest(`/sessions/${sessionId}/end`, {
          method: 'POST'
        });

        localStorage.removeItem('currentSessionId');
        updateProgress(results);
        
        // Navigate to results page with session results and questions
        navigate('/results', { 
          state: { 
            sessionResults: results,
            questions: questions
          } 
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setCompletingSession(false);
      }
    } else {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handleShowHint = () => {
    setShowHint(true);
    setHintsUsed(prev => prev + 1);
  };

  if (loading || userLoading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className={styles.container}>
      {completingSession && (
        <div className={styles.overlay}>
          <Loader size="large" text="Analyzing your results..." />
        </div>
      )}
      <div className={styles.card}>
        <AITutor 
          currentQuestion={currentQ}
          userAnswer={userInput}
          isCorrect={feedback?.isCorrect}
        />
        <div className={styles.header}>
          <h2 className={styles.title}>Definition Spelling</h2>
          <span className={styles.questionCount}>
            Question {currentQuestion + 1} of {questions.length}
          </span>
        </div>

        <div className={styles.progressContainer}>
          <div className={styles.progressBar} style={{ width: `${progress}%` }} />
        </div>

        <div className={styles.definitionContainer}>
          <h2><strong>Spell the word</strong></h2>
          <div className={`${styles.definition} ${styles.definitionSpelling}`}>
            <h3>Definition:</h3>
            <p>{currentQ?.content.definition}</p>
          </div>

          <div className={styles.hintContainer}>
            <p><strong>Hint:</strong> {currentQ?.content.hints[0]}</p>
          </div>
          
          {/* {!showHint && currentQ?.content.hints && (
            <button 
              onClick={handleShowHint}
              className={styles.hintButton}
              disabled={showExplanation}
            >
              Show Hint ({currentQ?.content.hints.length - hintsUsed} remaining)
            </button>
          )} */}

          {/* {showHint && currentQ?.content.hints && (
            <div className={styles.hint}>
              <h3>Hint:</h3>
              <p>{currentQ.content.hints[hintsUsed - 1]}</p>
            </div>
          )} */}
        </div>

        <form onSubmit={handleSubmit} className={styles.inputForm}>
          <input
            type="text"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            className={styles.answerInput}
            placeholder="Spell the word..."
            disabled={showExplanation}
          />
          {!showExplanation && (
            <button 
              type="submit" 
              className={styles.submitButton}
              disabled={isSubmitting || !userInput.trim()}
            >
              Submit
            </button>
          )}
        </form>

        {feedback && (
          <div className={`${styles.feedback} ${feedback.isCorrect ? styles.correct : styles.incorrect}`}>
            {feedback.message}
          </div>
        )}

        {showExplanation && (
          <>
            <div className={styles.explanation}>
              <p>Correct spelling: {currentQ.content.correctAnswer}</p>
              <p>{currentQ.content.explanation}</p>
              <div className={styles.usageExample}>
                <h3>Usage Example:</h3>
                <p>{currentQ.content.usageExample}</p>
              </div>
              {currentQ.content.wordFamily && (
                <div className={styles.wordFamily}>
                  <h3>Word Family:</h3>
                  <p>{currentQ.content.wordFamily}</p>
                </div>
              )}
            </div>
            <button 
              className={styles.nextButton}
              onClick={handleNext}
            >
              {currentQuestion + 1 >= questions.length ? 'Finish' : 'Next Word'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default DefinitionSpellingMode; 